import { css, cx, keyframes } from '@emotion/css';
import Tippy from '@tippyjs/react';
import { forwardRef, useEffect } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Link } from 'react-router-dom';

import { useSnowplow } from 'ms-helpers/Snowplow/useSnowplow';
import { Milo } from 'ms-pages/StudentDashboard/SunflowerStudentDashboard/Milo/Milo';
import { fontFamily } from 'ms-styles/base';
import { colors } from 'ms-styles/colors';
import type { FixGeneratedKey } from 'ms-utils/relay/relay-compiler-language-typescript-fixes';
import { useTypedRive } from 'ms-utils/rive/useTypedRive';
import { getStudentShopUrl } from 'ms-utils/urls';

import type { CustomMilo_student$key } from './__generated__/CustomMilo_student.graphql';
import customMiloRiv from './custom_milo.riv';
import { NO_ACCESSORY_SYSTEM_ID } from './shopConstants';
import { artboard, stateMachine } from './types';

// eslint-disable-next-line import/no-extraneous-dependencies
import 'tippy.js/dist/tippy.css';

export function CustomMilo({
  className,
  studentKey,
}: {
  className?: string;
  studentKey: FixGeneratedKey<CustomMilo_student$key>;
}) {
  const { trackStructEvent } = useSnowplow();
  const { gamification } = useFragment(
    graphql`
      fragment CustomMilo_student on Student {
        gamification {
          accessory {
            systemId
            riveFile
          }
        }
      }
    `,
    studentKey,
  );
  const accessory = gamification?.accessory ?? null;
  const isNoAccessory = accessory?.systemId === NO_ACCESSORY_SYSTEM_ID;
  const riveFile = accessory?.riveFile ?? '';

  const { RiveComponent, rive } = useTypedRive({
    src: customMiloRiv,
    artboard,
    stateMachine,
    autoplay: true,
  });

  useEffect(() => {
    if (isNoAccessory) {
      rive?.fire('ENTRY');
    } else {
      const inputName = `${riveFile}_IDLE`;
      rive?.unsafeFire(inputName);
    }
  }, [rive, riveFile, isNoAccessory]);

  return (
    <div className={cx(rootCss, className)}>
      <div
        // The extra div allows us to work with percentages when positioning the
        // animations relative to Milo.
        style={{
          height: '100%',
          marginLeft: '44%',
          marginTop: '8%',
          position: 'relative',
          width: '100%',
        }}
      >
        <RiveComponent />
        <Tippy
          className={tooltipCss}
          content="Shop for new items"
          disabled={!isNoAccessory}
        >
          <Link
            // Using an extra container prevents Tippy from being positioned
            // relative to a transformed click area.
            className={clickAreaWrapperCss}
            style={{ opacity: isNoAccessory ? 1 : undefined }}
            to={getStudentShopUrl()}
            onClick={() =>
              trackStructEvent({
                category: 'gamification/shop',
                action: 'clicked_milo_accessory_in_header',
              })
            }
            onMouseEnter={() => {
              if (isNoAccessory) return;
              rive?.unsafeFire(`${riveFile}_ACTIVE`);
            }}
          >
            <ClickAreaSvg className={clickAreaCss} />
          </Link>
        </Tippy>
      </div>
    </div>
  );
}

/**
 * Used in the Student Dashboard V1.
 */
export function CustomMiloForV1({
  studentKey,
}: {
  studentKey: FixGeneratedKey<CustomMilo_student$key>;
}) {
  return (
    // Flex forces any vertical alignment on the Milo image to be ignored
    <div style={{ display: 'flex', position: 'relative' }}>
      <Milo dynamic />
      <CustomMilo studentKey={studentKey} />
    </div>
  );
}

export const ClickAreaSvg = forwardRef<
  SVGSVGElement,
  React.SVGProps<SVGSVGElement>
>(function ClickAreaSvg({ className, ...restProps }, ref) {
  return (
    <svg
      ref={ref}
      width="48"
      height="48"
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cx(clickAreaCss, className)}
      {...restProps}
    >
      <circle
        cx="24"
        cy="24"
        r="22"
        fill="white"
        fillOpacity="0.32"
        stroke="currentColor"
        strokeWidth="1"
        strokeDasharray="2.3"
      />
    </svg>
  );
});

const spin = keyframes({
  '0': {
    transform: 'rotate(0)',
  },

  '100%': {
    transform: 'rotate(360deg)',
  },
});

const pulse = keyframes({
  '0': {
    transform: 'scale(1)',
  },

  '50%': {
    transform: 'scale(1.1)',
  },

  '100%': {
    transform: 'scale(1)',
  },
});

// Position the element next to Milo on the bottom left
const rootCss = css({
  bottom: 0,
  height: 100,
  position: 'absolute',
  right: '100%',
  width: 100,
});

const tooltipCss = css({
  '&.tippy-box': {
    backgroundColor: 'transparent',
  },

  '.tippy-content': {
    alignItems: 'center',
    backgroundColor: colors.grey,
    borderRadius: 6,
    color: 'white',
    display: 'flex',
    fontFamily: fontFamily.body,
    fontSize: 12,
    fontWeight: 600,
    gap: 4,
    lineHeight: 1.2,
    padding: 8,
  },

  '.tippy-arrow': {
    color: colors.grey,
  },
});

const clickAreaWrapperCss = css({
  cursor: 'pointer',
  height: 48,
  inset: 0,
  margin: 'auto auto 14.5%',
  opacity: 0,
  position: 'absolute',
  width: 48,
});

const clickAreaCss = css({
  animationComposition: 'accumulate',
  animationDuration: '10s, 2s',
  animationIterationCount: 'infinite',
  // The order of the animations matters here. I'm not sure why.
  animationName: `${spin}, ${pulse}`,
  animationTimingFunction: 'linear, ease-in-out',
  color: colors.eggplant,
  inset: 0,
  position: 'absolute',
  transition: 'scale .2s',

  ':hover': {
    animationPlayState: 'paused',
    // Allows for an accumulative effect on top of the transforms set by the
    // animations. It's not as well supported as transform, but it's a hover
    // effect, so who cares right?
    scale: '1.33',
  },
});
